
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaSave, FaUndo } from "react-icons/fa";
import ToastMessage from './ToastMessage';

function Results() {
    const [seriesList, setSeriesList] = useState([]);
    const [results, setResults] = useState([]);
    const [editSlot, setEditSlot] = useState(null);
    const [form, setForm] = useState({});
    const [loadingSlot, setLoadingSlot] = useState({});
    const [loadingSingle, setLoadingSingle] = useState({});
    const [toast, setToast] = useState({ type: '', message: '' });
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

    const apiBase = process.env.REACT_APP_SERVER_URL;

    useEffect(() => {
        fetchSeries();
        fetchResults();
    }, [selectedDate]);

    const fetchSeries = async () => {
        try {
            const res = await axios.get(`${apiBase}/api/admin/series`);
           // console.log('res.data',res.data);
            
            setSeriesList(res.data);
        } catch (err) {
            console.error("Error fetching series:", err);
        }
    };

    const fetchResults = async () => {
        try {
            const res = await axios.get(`${apiBase}/api/results`, {
                params: { date: selectedDate }
            });

           // console.log('res', res);
            setResults(res.data);
        } catch (err) {
            console.error("Error fetching results:", err);
        }
    };
    const [slotConfig, setSlotConfig] = useState(null);

    useEffect(() => {
        const fetchSlotConfig = async () => {
            try {
                const res = await axios.get(`${apiBase}/api/slot-set`);
                setSlotConfig(res.data);
            } catch (err) {
                console.error("Error fetching slot settings:", err);
            }
        };
        fetchSlotConfig();
    }, []);

    const generateSlots = () => {
        if (!slotConfig) return [];

        const slots = [];
        const startParts = slotConfig.start_time.split(":");
        const endParts = slotConfig.end_time.split(":");

        let start = parseInt(startParts[0]) * 60 + parseInt(startParts[1]); // minutes from midnight
        let end = parseInt(endParts[0]) * 60 + parseInt(endParts[1]);
        const interval = slotConfig.interval_minutes;

        // Handle crossing midnight (e.g. 23:30 → 00:00)
        if (end <= start) {
            end += 24 * 60; // add 24 hours to end
        }

        while (start <= end) {
            const hours = Math.floor((start % (24 * 60)) / 60);
            const minutes = start % 60;
            const timeLabel = `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
            slots.push(timeLabel);
            start += interval;
        }

        return slots;
    };



    const slots = generateSlots();

    const handleChange = (e, seriesId, slot) => {
        setForm({
            ...form,
            [slot]: {
                ...form[slot],
                [seriesId]: e.target.value
            }
        });
    };

    const handleEditSlot = (slot) => {
        const slotData = {};
        seriesList.forEach(series => {
            const existingResult = results.find(r => r.seriesId === series.id && r.slot === slot);
            slotData[series.id] = existingResult?.winning_number || '';
        });
        setForm(prev => ({ ...prev, [slot]: slotData }));
        setEditSlot(slot);
    };

    // Save a single series in a slot
    const handleSaveSingle = async (slot, seriesId) => {
        if (!form[slot]?.[seriesId]) return;
        const key = `${slot}-${seriesId}`;
        setLoadingSingle(prev => ({ ...prev, [key]: true }));

        try {
            const existingResult = results.find(r => r.seriesId === parseInt(seriesId) && r.slot === slot);

            if (existingResult) {
                await axios.put(`${apiBase}/api/results/${existingResult.id}`, {
                    seriesId,
                    winning_number: form[slot][seriesId],
                    draw_date: selectedDate.split('T')[0],
                    slot
                });
            } else {
                await axios.post(`${apiBase}/api/results`, {
                    seriesId,
                    winning_number: form[slot][seriesId],
                    draw_date: selectedDate.split('T')[0],
                    slot
                });
            }

            setToast({ type: 'success', message: `Saved ${seriesList.find(s => s.id === parseInt(seriesId)).name} in ${slot}` });
            fetchResults();
        } catch (err) {
            console.error(err);
            setToast({ type: 'error', message: 'Failed to save.' });
        } finally {
            setLoadingSingle(prev => ({ ...prev, [key]: false }));
        }
    };

    // Save all series in a slot
    const handleSaveSlot = async (slot) => {
        if (!form[slot]) return;
        setLoadingSlot(prev => ({ ...prev, [slot]: true }));

        try {
            for (const seriesId in form[slot]) {
                const winning_number = form[slot][seriesId];
                if (!winning_number) continue;
                const existingResult = results.find(r => r.seriesId === parseInt(seriesId) && r.slot === slot);

                if (existingResult) {
                    await axios.put(`${apiBase}/api/results/${existingResult.id}`, {
                        seriesId,
                        winning_number,
                        draw_date: selectedDate.split('T')[0],
                        slot
                    });
                } else {
                    await axios.post(`${apiBase}/api/results`, {
                        seriesId,
                        winning_number,
                        draw_date: selectedDate.split('T')[0],
                        slot
                    });
                }
            }
            setToast({ type: 'success', message: `Slot ${slot} saved successfully!` });
            setEditSlot(null);
            fetchResults();
        } catch (err) {
            console.error(err);
            setToast({ type: 'error', message: 'Failed to save slot.' });
        } finally {
            setLoadingSlot(prev => ({ ...prev, [slot]: false }));
        }
    };

    const handleReset = (slot, seriesId) => {
        setForm(prev => ({
            ...prev,
            [slot]: {
                ...prev[slot],
                [seriesId]: ''
            }
        }));
    };

    const handleDelete = async (slot) => {
        if (!window.confirm(`Delete all results for ${slot}?`)) return;
        try {
            const toDelete = results.filter(r => r.slot === slot);
            for (const r of toDelete) {
                await axios.delete(`${apiBase}/api/results/${r.id}`);
            }
            setToast({ type: 'success', message: `Slot ${slot} deleted successfully!` });
            fetchResults();
        } catch (err) {
            console.error(err);
            setToast({ type: 'error', message: 'Failed to delete slot.' });
        }
    };

    return (
        <div className="container py-5">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h3>📊 Results Schedule</h3>
                <input
                    type="date"
                    className="form-control"
                    style={{ width: "200px" }}
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                />
            </div>

            <div className="table-responsive">
                <table className="table table-bordered table-striped text-center align-middle">
                    <thead>
                        <tr>
                            <th>Time</th>
                            {seriesList.map(s => <th key={s.id}>{s.name}</th>)}
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {slots.map(slot => {
                            const isEditing = editSlot === slot;
                            return (
                                <tr key={slot}>
                                    <td>{slot}</td>
                                    {seriesList.map(series => {
                                        const existingResult = results.find(r => r.seriesId === series.id && r.slot === slot);
                                        const value = form[slot]?.[series.id] ?? existingResult?.winning_number ?? '';
                                        const key = `${slot}-${series.id}`;
                                        return (
                                            <td key={series.id}>
                                                {isEditing ? (
                                                    <div>
                                                        <input
                                                            type="number"
                                                            className="form-control text-center mb-1"
                                                            maxLength={2}
                                                            value={value}
                                                            onChange={(e) => handleChange(e, series.id, slot)}
                                                            placeholder="00"
                                                        />
                                                        <div className="d-flex justify-content-center gap-1">
                                                            <button
                                                                className="btn btn-sm btn-success"
                                                                onClick={() => handleSaveSingle(slot, series.id)}
                                                                disabled={loadingSingle[key]}
                                                            >
                                                                <FaSave />
                                                            </button>
                                                            <button
                                                                className="btn btn-sm btn-warning"
                                                                onClick={() => handleReset(slot, series.id)}
                                                            >
                                                                <FaUndo />
                                                            </button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    value || "-"
                                                )}
                                            </td>
                                        );
                                    })}
                                    <td>
                                        {isEditing ? (
                                            <div className="d-flex justify-content-center gap-2">
                                                <button
                                                    className="btn btn-success btn-sm"
                                                    onClick={() => handleSaveSlot(slot)}
                                                    disabled={loadingSlot[slot]}
                                                >
                                                    <FaSave /> Save All
                                                </button>
                                                <button
                                                    className="btn btn-secondary btn-sm"
                                                    onClick={() => setEditSlot(null)}
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="d-flex justify-content-center gap-1">
                                                <button
                                                    className="btn btn-primary btn-sm"
                                                    onClick={() => handleEditSlot(slot)}
                                                >
                                                    Edit
                                                </button>
                                                <button
                                                    className="btn btn-danger btn-sm"
                                                    onClick={() => handleDelete(slot)}
                                                >
                                                    Delete
                                                </button>
                                            </div>
                                        )}
                                    </td>

                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {toast.message && <ToastMessage type={toast.type} message={toast.message} />}
        </div>
    );
}

export default Results;
